// Builds the frozen follow-up plan (provider sharing -> contact access ->
// sensor calibration -> notification rules) persisted before completion and applied afterwards
// through the claim-scoped operations endpoint.

export function canonicalJson(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

// 2x32-bit FNV-1a: stable, dependency-free, collision-safe enough for IDs
// scoped to one claim.
function hash(text) {
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ 0x5bd1e995;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x5bd1e995) >>> 0;
  }
  return `${a.toString(16).padStart(8, "0")}${b.toString(16).padStart(8, "0")}`;
}

// Payload-bound operation ID: an identical retry reuses the ID, a corrected
// intent gets a new one.
export function operationId(claimId, kind, payload) {
  return `c${claimId}-${kind}-${hash(canonicalJson({ kind, payload }))}`;
}

// Must match the server's phase order. Calibration authorization does not
// depend on contact access succeeding; each operation reports separately.
const PHASE = { provider_share: 0, contact_access: 1, level_calibration: 2, notification_rule: 3 };

export const OPERATION_KIND_LABEL = {
  provider_share: "Provider sharing",
  contact_access: "Contact device access",
  level_calibration: "Sensor calibration",
  notification_rule: "Alert rules",
};

// The four reviewed calibration fields, in display order, with units.
export const CALIBRATION_FIELDS = [
  ["sensor_mount_type", "Sensor mount", ""],
  ["sensor_height_offset", "Floor to sensor face", "in"],
  ["scale_min_eng", "Empty point (0%)", "in"],
  ["scale_max_eng", "Full point (100%)", "in"],
];

function normalizeKind(kind) {
  return kind === "sms" || kind === "email" || kind === "push" ? kind : null;
}

export function findActiveStatus(lookups) {
  const statuses = lookups?.statuses || [];
  const active = statuses.filter((row) => String(row.label || "").trim().toLowerCase() === "active");
  return active.length === 1 ? active[0] : null;
}

export function findRecipientType(lookups, scope) {
  const rows = (lookups?.recipient_types || []).filter((row) => row.kind === scope);
  return rows.length === 1 ? rows[0] : null;
}

export function findMethod(lookups, kind) {
  const rows = (lookups?.methods || []).filter((row) => row.kind === kind);
  return rows.length === 1 ? rows[0] : null;
}

// Default subtype for an alert type: the configured non-"Resent" subtype
// when exactly one exists; otherwise the installer must choose.
export function defaultSubtype(lookups, alertTypeId) {
  const rows = (lookups?.subtypes || []).filter((row) => Number(row.alert_type_id) === Number(alertTypeId));
  if (rows.length === 1) return rows[0];
  const plain = rows.filter((row) => String(row.label).trim().toLowerCase() !== "resent");
  return plain.length === 1 ? plain[0] : null;
}

/**
 * @param {object} input
 * @param {number} input.claimId
 * @param {number} input.assetId                 canonical installed asset
 * @param {number[]} input.providerIds          providers selected for the asset
 * @param {Array} input.contactAccess            [{contact_id?, client_ref?, email?, phone?, enabled, access_type, service_access_type}]
 * @param {object} input.alerts                 {enabled, recipients:[{scope, contact_id?, provider_id?, client_ref?, kind, value}], subtypeByAlertType}
 * @param {Array} input.thresholds              submitted thresholds (for alert types)
 * @param {object} input.lookups                claim-setup-options lookups
 * @param {object|null} input.levelCalibration  reviewed four-field calibration (claim-scoped operation path only)
 */
export function buildOperationsPlan({ claimId, assetId, providerIds = [], contactAccess = [], alerts, thresholds = [], lookups, levelCalibration = null }) {
  const errors = [];
  const operations = [];
  const add = (kind, payload) => operations.push({ operation_id: operationId(claimId, kind, payload), kind, payload });

  [...new Set(providerIds.map(Number))].sort((a, b) => a - b).forEach((providerId) => {
    add("provider_share", { provider_id: providerId, address_item_id: Number(assetId), enabled: true });
  });

  contactAccess
    .filter((row) => row.enabled)
    .forEach((row) => {
      const contact_ref = row.contact_id
        ? { contact_id: Number(row.contact_id) }
        : {
            client_ref: row.client_ref || null,
            email: row.email || null,
            phone: row.phone || null,
          };
      add("contact_access", {
        contact_ref,
        address_item_id: Number(assetId),
        enabled: true,
        access_type: row.access_type === "write" ? "write" : "read",
        service_access_type: row.service_access_type === "write" ? "write" : "read",
      });
    });

  // Measurements only: the server derives the device and installed asset from
  // the authorized session / completed claim, never from client-supplied IDs.
  if (levelCalibration) {
    add("level_calibration", {
      sensor_mount_type: levelCalibration.sensor_mount_type,
      sensor_height_offset: levelCalibration.sensor_height_offset,
      scale_min_eng: levelCalibration.scale_min_eng,
      scale_max_eng: levelCalibration.scale_max_eng,
    });
  }

  if (alerts?.enabled) {
    const alertTypeIds = [
      ...new Set(
        thresholds
          .filter((row) => row.alert_enabled && row.alert_type_id != null)
          .map((row) => Number(row.alert_type_id)),
      ),
    ].sort((a, b) => a - b);
    const status = findActiveStatus(lookups);
    if (!status) errors.push("No single Active alert status is configured; notifications cannot be saved.");
    if (!alertTypeIds.length) errors.push("Turn on Alert for at least one threshold, or turn alerts off.");
    (alerts.recipients || []).forEach((recipient) => {
      const kind = normalizeKind(recipient.kind);
      const method = kind ? findMethod(lookups, kind) : null;
      const sendTo = findRecipientType(lookups, recipient.scope);
      const who = recipient.label || recipient.value;
      if (!method) {
        errors.push(`No configured delivery method matches ${kind || "this destination"} for ${who}.`);
        return;
      }
      if (!sendTo) {
        errors.push(`No configured recipient type matches ${recipient.scope} for ${who}.`);
        return;
      }
      if (!status) return;
      alertTypeIds.forEach((alertTypeId) => {
        const chosen = alerts.subtypeByAlertType?.[alertTypeId];
        const subtype = chosen
          ? (lookups?.subtypes || []).find((row) => Number(row.id) === Number(chosen))
          : defaultSubtype(lookups, alertTypeId);
        if (!subtype) {
          errors.push(`Choose an alert subtype for alert type ${alertTypeId}.`);
          return;
        }
        add("notification_rule", {
          client_ref: recipient.client_ref || null,
          alert_type_id: alertTypeId,
          alert_sub_type_id: Number(subtype.id),
          event_type_id: Number(method.id),
          send_to_type_id: Number(sendTo.id),
          status_type_id: Number(status.id),
          module_id: Number(status.module_id),
          recipient: {
            scope: recipient.scope,
            contact_id: recipient.contact_id ? Number(recipient.contact_id) : null,
            provider_id: recipient.provider_id ? Number(recipient.provider_id) : null,
            client_ref: recipient.client_ref || null,
            destination_ref: { kind, value: String(recipient.value).trim() },
          },
          frequency: null,
          template_source: null,
          template_id: null,
        });
      });
    });
  }

  const unique = [];
  const seen = new Set();
  operations.forEach((operation) => {
    if (seen.has(operation.operation_id)) return;
    seen.add(operation.operation_id);
    unique.push(operation);
  });
  unique.sort((a, b) => PHASE[a.kind] - PHASE[b.kind]);
  return { operations: unique, errors: [...new Set(errors)] };
}

export function buildSetupBody({ entityId, assetId, providerIds = [], contactAccess = [], notificationRules = [], operations }) {
  return {
    version: 1,
    target_entity_id: Number(entityId) || null,
    target_address_item_id: Number(assetId) || null,
    provider_shares: [...new Set(providerIds.map(Number))].map((providerId) => ({
      provider_id: providerId,
      address_item_id: Number(assetId) || null,
      asset_client_ref: null,
      enabled: true,
    })),
    contact_access: contactAccess.map((row) => ({
      client_ref: (row.client_ref || (row.contact_id ? null : `contact-${row.email || row.phone || "new"}`))?.slice(0, 64) ?? null,
      contact_id: row.contact_id ? Number(row.contact_id) : null,
      email: row.email || null,
      phone: row.phone || null,
      address_item_id: Number(assetId) || null,
      asset_client_ref: null,
      enabled: Boolean(row.enabled),
      access_type: row.access_type === "write" ? "write" : "read",
      service_access_type: row.service_access_type === "write" ? "write" : "read",
    })),
    notification_rules: notificationRules.map((operation) => ({
      ...operation.payload,
      client_ref: operation.payload.client_ref || operation.operation_id,
    })),
    operations,
  };
}

// Merge server results into the plan, keeping prior saved results.
export function mergeOperationResults(plan, results) {
  const byId = new Map((results || []).map((row) => [row.operation_id, row]));
  return plan.map((operation) => {
    const result = byId.get(operation.operation_id);
    if (!result) return { ...operation, status: operation.status || "pending" };
    return { ...operation, ...result, payload: operation.payload };
  });
}

export function operationsToRetry(operations) {
  return (operations || [])
    .filter((row) => row.status === "failed" || row.status === "pending" || row.status === "processing")
    .map(({ operation_id, kind, payload }) => ({ operation_id, kind, payload }));
}

export function setupState(operations) {
  const rows = operations || [];
  if (!rows.length) return "none";
  if (rows.some((row) => row.status === "failed")) return "needs_attention";
  if (rows.some((row) => row.status !== "saved")) return "pending";
  return "complete";
}

export const OPERATION_STATUS_LABEL = {
  saved: "Complete",
  failed: "Needs retry",
  pending: "Pending",
  processing: "Pending",
  not_selected: "Not selected",
};
